import { baseApi } from "../api";
import {
  CatalogueSearchResult,
  CreateListedInstrumentPayload,
  InstrumentPrice,
  DiscoveredListings,
  DiscoverListingsPayload,
  InstrumentSearchResult,
  InstrumentSummary,
  isCatalogueSearchResult,
  PriceOverride,
  RecordedPriceOverride,
  RecordInstrumentPricePayload,
  RecordPriceOverridePayload,
  RepairInstrumentListingPayload,
  ResolveInstrumentPayload,
  ResolvedInstrument,
  SetInstrumentSourceCountryPayload,
} from "./types";
import { priceOverrideRipple } from "../api/tags";

// One sentinel tag covers every search cache entry regardless of term typed, since a resolve can
// add a row that any term might now match.
const searchTags = [{ type: "Instrument" as const, id: "SEARCH" }];
const instrumentCreatedTags = [...searchTags, "AdminInstrument" as const];

// `resolveInstrument` is a mutation, not a query: it's resolve-or-create and rate limited
// (20/15min), so it must only be wired to an explicit action, never keystrokes — same for `discoverListings`.
export const instrumentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    searchInstruments: builder.query<InstrumentSearchResult[], string>({
      query: (search) => ({ url: "/instruments", params: { search } }),
      providesTags: searchTags,
    }),

    // A separate endpoint so it can stay silent: searchInstruments' toast is the Buy panel's only error message.
    searchCatalogInstruments: builder.query<CatalogueSearchResult[], string>({
      query: (search) => ({ url: "/instruments", params: { search } }),
      transformResponse: (results: InstrumentSearchResult[]) =>
        results.filter(isCatalogueSearchResult),
      providesTags: searchTags,
    }),

    resolveInstrument: builder.mutation<
      ResolvedInstrument,
      ResolveInstrumentPayload
    >({
      query: (payload) => ({
        url: "/instruments",
        method: "post",
        data: payload,
      }),
      invalidatesTags: instrumentCreatedTags,
    }),

    discoverListings: builder.query<
      DiscoveredListings,
      DiscoverListingsPayload
    >({
      query: ({ symbol, exchange }) => ({
        url: "/instruments/listings",
        params: { symbol, exchange },
      }),
    }),

    createListedInstrument: builder.mutation<
      ResolvedInstrument,
      CreateListedInstrumentPayload
    >({
      query: (payload) => ({
        url: "/instruments/listings",
        method: "post",
        data: payload,
      }),
      invalidatesTags: instrumentCreatedTags,
    }),

    getPriceOverrides: builder.query<PriceOverride[], number>({
      query: (instrumentId) => ({
        url: `/instruments/${instrumentId}/overrides`,
      }),
      providesTags: (_result, _error, instrumentId) => [
        { type: "PriceOverride" as const, id: instrumentId },
      ],
    }),

    recordPriceOverride: builder.mutation<
      RecordedPriceOverride,
      RecordPriceOverridePayload
    >({
      query: ({ instrumentId, date, priceNative, fxToEur }) => ({
        url: `/instruments/${instrumentId}/overrides`,
        method: "post",
        data: {
          date,
          priceNative,
          ...(fxToEur === undefined ? {} : { fxToEur }),
        },
      }),
      invalidatesTags: (_result, _error, { instrumentId }) => [
        { type: "PriceOverride" as const, id: instrumentId },
        ...priceOverrideRipple(),
      ],
    }),

    // Valuations never read the country, so only the holdings rows, an open report and the admin count refetch.
    setInstrumentSourceCountry: builder.mutation<
      InstrumentSummary,
      SetInstrumentSourceCountryPayload
    >({
      query: ({ instrumentId, sourceCountry }) => ({
        url: `/instruments/${instrumentId}/source-country`,
        method: "put",
        data: { sourceCountry },
      }),
      invalidatesTags: [
        "Holding",
        "Report",
        "AdminOverview",
        "AdminInstrument",
        "AdminInstrumentTodo",
        "AdminAuditLog",
      ],
    }),

    repairInstrumentListing: builder.mutation<
      InstrumentSummary,
      RepairInstrumentListingPayload
    >({
      query: ({ instrumentId, ...repair }) => ({
        url: `/instruments/${instrumentId}`,
        method: "patch",
        data: repair,
      }),
      invalidatesTags: [
        "AdminOverview",
        "AdminInstrument",
        "AdminInstrumentTodo",
        "AdminAuditLog",
      ],
    }),

    recordInstrumentPrice: builder.mutation<
      InstrumentPrice,
      RecordInstrumentPricePayload
    >({
      query: ({ instrumentId, date, priceNative, fxToEur }) => ({
        url: `/instruments/${instrumentId}/prices`,
        method: "post",
        data: {
          date,
          priceNative,
          ...(fxToEur === undefined ? {} : { fxToEur }),
        },
      }),
      // A price alone leaves the row on the to-do list; the marking write that follows refetches it.
      invalidatesTags: ["AdminOverview", "AdminInstrument", "AdminAuditLog"],
    }),

    deletePriceOverride: builder.mutation<
      void,
      { instrumentId: number; date: string }
    >({
      query: ({ instrumentId, date }) => ({
        url: `/instruments/${instrumentId}/overrides/${date}`,
        method: "delete",
      }),
      invalidatesTags: (_result, _error, { instrumentId }) => [
        { type: "PriceOverride" as const, id: instrumentId },
        ...priceOverrideRipple(),
      ],
    }),
  }),
});

export const {
  useSearchInstrumentsQuery,
  useLazySearchInstrumentsQuery,
  useSearchCatalogInstrumentsQuery,
  useResolveInstrumentMutation,
  useLazyDiscoverListingsQuery,
  useCreateListedInstrumentMutation,
  useGetPriceOverridesQuery,
  useRecordPriceOverrideMutation,
  useDeletePriceOverrideMutation,
  useSetInstrumentSourceCountryMutation,
  useRepairInstrumentListingMutation,
  useRecordInstrumentPriceMutation,
} = instrumentsApi;
